import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import {
    UserGroupIcon,
    KeyIcon,
    NoSymbolIcon,
    CheckCircleIcon,
    XMarkIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

const AdminPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { isAdmin, isLoading: authLoading } = useAuth();

    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [resetPasswordModal, setResetPasswordModal] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [actionLoading, setActionLoading] = useState(null);

    const styles = {
        container: {
            padding: '2rem',
            maxWidth: '1200px',
            margin: '0 auto',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '2rem',
        },
        headerIcon: {
            width: '32px',
            height: '32px',
            color: '#5856D6',
        },
        title: {
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#1D1D1F',
        },
        card: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        },
        sectionTitle: {
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#1D1D1F',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '12px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            fontSize: '0.85rem',
            fontWeight: '600',
            color: '#86868B',
        },
        td: {
            padding: '12px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
            fontSize: '0.9rem',
            color: '#1D1D1F',
        },
        statusBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.8rem',
            fontWeight: '500',
        },
        statusActive: {
            backgroundColor: 'rgba(52, 199, 89, 0.1)',
            color: '#34C759',
        },
        statusDisabled: {
            backgroundColor: 'rgba(255, 59, 48, 0.1)',
            color: '#FF3B30',
        },
        roleBadge: {
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.8rem',
            fontWeight: '500',
            backgroundColor: 'rgba(88, 86, 214, 0.1)',
            color: '#5856D6',
        },
        actionButton: {
            padding: '6px 12px',
            borderRadius: '8px',
            border: 'none',
            fontSize: '0.8rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            marginRight: '8px',
        },
        resetButton: {
            backgroundColor: 'rgba(0, 122, 255, 0.1)',
            color: '#007AFF',
        },
        disableButton: {
            backgroundColor: 'rgba(255, 59, 48, 0.1)',
            color: '#FF3B30',
        },
        enableButton: {
            backgroundColor: 'rgba(52, 199, 89, 0.1)',
            color: '#34C759',
        },
        error: {
            backgroundColor: 'rgba(255, 59, 48, 0.1)',
            color: '#FF3B30',
            padding: '12px',
            borderRadius: '10px',
            fontSize: '0.85rem',
            marginBottom: '1rem',
            textAlign: 'center',
        },
        success: {
            backgroundColor: 'rgba(52, 199, 89, 0.1)',
            color: '#34C759',
            padding: '12px',
            borderRadius: '10px',
            fontSize: '0.85rem',
            marginBottom: '1rem',
            textAlign: 'center',
        },
        loading: {
            textAlign: 'center',
            padding: '3rem',
            color: '#86868B',
        },
        noAccess: {
            textAlign: 'center',
            padding: '4rem',
        },
        noAccessTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#1D1D1F',
            marginBottom: '1rem',
        },
        noAccessText: {
            color: '#86868B',
            marginBottom: '1.5rem',
        },
        backButton: {
            padding: '10px 20px',
            backgroundColor: '#007AFF',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
        },
        // Modal styles
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
        },
        modalTitle: {
            fontSize: '1.2rem',
            fontWeight: '600',
            color: '#1D1D1F',
        },
        modalClose: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
        },
        modalCloseIcon: {
            width: '20px',
            height: '20px',
            color: '#86868B',
        },
        input: {
            width: '100%',
            padding: '12px 14px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
            fontSize: '0.95rem',
            outline: 'none',
            marginBottom: '1rem',
        },
        modalActions: {
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
        },
        modalCancel: {
            padding: '10px 20px',
            backgroundColor: '#E5E5EA',
            color: '#1D1D1F',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
        },
        modalConfirm: {
            padding: '10px 20px',
            backgroundColor: '#007AFF',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.9rem',
            fontWeight: '500',
            cursor: 'pointer',
        },
        refreshButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: '#1D1D1F',
            cursor: 'pointer',
            marginLeft: 'auto',
        },
        refreshIcon: {
            width: '16px',
            height: '16px',
        },
    };

    useEffect(() => {
        if (!authLoading && isAdmin) {
            loadUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isAdmin]);

    const loadUsers = async () => {
        setIsLoading(true);
        setError('');
        try {
            const data = await apiService.getUsers();
            setUsers(data.users || []);
        } catch (err) {
            setError(err.response?.data?.message || t('admin.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword || newPassword.length < 8) {
            setError(t('auth.passwordTooShort'));
            return;
        }

        setActionLoading(resetPasswordModal.id);
        try {
            await apiService.resetUserPassword(resetPasswordModal.id, newPassword);
            setSuccess(t('admin.passwordResetSuccess'));
            setResetPasswordModal(null);
            setNewPassword('');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || t('auth.error'));
        } finally {
            setActionLoading(null);
        }
    };

    const handleDisableUser = async (userId) => {
        setActionLoading(userId);
        setError('');
        try {
            await apiService.disableUser(userId);
            setSuccess(t('admin.userDisabled'));
            loadUsers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || t('auth.error'));
        } finally {
            setActionLoading(null);
        }
    };

    const handleEnableUser = async (userId) => {
        setActionLoading(userId);
        setError('');
        try {
            await apiService.enableUser(userId);
            setSuccess(t('admin.userEnabled'));
            loadUsers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || t('auth.error'));
        } finally {
            setActionLoading(null);
        }
    };

    // Wait for auth to load
    if (authLoading) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.loading}>{t('home.loading')}</div>
                </div>
            </div>
        );
    }

    // Check admin access
    if (!isAdmin) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <div style={styles.noAccess}>
                        <h2 style={styles.noAccessTitle}>{t('admin.noAccess')}</h2>
                        <p style={styles.noAccessText}>{t('admin.noAccessDesc')}</p>
                        <button style={styles.backButton} onClick={() => navigate('/')}>
                            {t('auth.backToHome')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <UserGroupIcon style={styles.headerIcon} />
                <h1 style={styles.title}>{t('admin.title')}</h1>
            </div>

            {error && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.success}>{success}</div>}

            <div style={styles.card}>
                <div style={styles.sectionTitle}>
                    <UserGroupIcon style={{ width: '20px', height: '20px' }} />
                    {t('admin.userManagement')}
                    <button style={styles.refreshButton} onClick={loadUsers}>
                        <ArrowPathIcon style={styles.refreshIcon} />
                        {t('home.refresh')}
                    </button>
                </div>

                {isLoading ? (
                    <div style={styles.loading}>{t('home.loading')}</div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>ID</th>
                                <th style={styles.th}>{t('auth.email')}</th>
                                <th style={styles.th}>{t('admin.role')}</th>
                                <th style={styles.th}>{t('admin.status')}</th>
                                <th style={styles.th}>{t('admin.createdAt')}</th>
                                <th style={styles.th}>{t('admin.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id}>
                                    <td style={styles.td}>{user.id}</td>
                                    <td style={styles.td}>{user.email}</td>
                                    <td style={styles.td}>
                                        <span style={styles.roleBadge}>
                                            {user.role === 'admin' ? t('admin.adminRole') : t('admin.userRole')}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.statusBadge,
                                            ...(user.disabled ? styles.statusDisabled : styles.statusActive),
                                        }}>
                                            {user.disabled ? (
                                                <><NoSymbolIcon style={{ width: '14px', height: '14px' }} />{t('admin.disabled')}</>
                                            ) : (
                                                <><CheckCircleIcon style={{ width: '14px', height: '14px' }} />{t('admin.active')}</>
                                            )}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                                    </td>
                                    <td style={styles.td}>
                                        {user.role !== 'admin' && (
                                            <>
                                                <button
                                                    style={{ ...styles.actionButton, ...styles.resetButton }}
                                                    onClick={() => setResetPasswordModal(user)}
                                                    disabled={actionLoading === user.id}
                                                >
                                                    <KeyIcon style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                                                    {t('admin.resetPassword')}
                                                </button>
                                                {user.disabled ? (
                                                    <button
                                                        style={{ ...styles.actionButton, ...styles.enableButton }}
                                                        onClick={() => handleEnableUser(user.id)}
                                                        disabled={actionLoading === user.id}
                                                    >
                                                        {t('admin.enable')}
                                                    </button>
                                                ) : (
                                                    <button
                                                        style={{ ...styles.actionButton, ...styles.disableButton }}
                                                        onClick={() => handleDisableUser(user.id)}
                                                        disabled={actionLoading === user.id}
                                                    >
                                                        {t('admin.disable')}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Reset Password Modal */}
            {resetPasswordModal && (
                <div style={styles.modalOverlay} onClick={() => setResetPasswordModal(null)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>{t('admin.resetPasswordFor')} {resetPasswordModal.email}</h3>
                            <button style={styles.modalClose} onClick={() => setResetPasswordModal(null)}>
                                <XMarkIcon style={styles.modalCloseIcon} />
                            </button>
                        </div>
                        <input
                            type="password"
                            placeholder={t('auth.newPassword')}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            style={styles.input}
                        />
                        <div style={styles.modalActions}>
                            <button style={styles.modalCancel} onClick={() => setResetPasswordModal(null)}>
                                {t('admin.cancel')}
                            </button>
                            <button
                                style={styles.modalConfirm}
                                onClick={handleResetPassword}
                                disabled={actionLoading === resetPasswordModal.id}
                            >
                                {t('admin.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPage;
